import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'horizontal';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block relative overflow-hidden rounded-xl cursor-pointer h-[420px] md:h-[500px]"
      >
        <img
          src={article.coverImage}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          <span className="inline-block px-3 py-1 bg-primary-500 text-white text-xs font-medium rounded-full mb-3">
            {article.categoryName}
          </span>
          <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-2 line-clamp-2 group-hover:text-primary-300 transition-colors">
            {article.title}
          </h3>
          <p className="text-white/70 text-sm line-clamp-2 mb-3">{article.excerpt}</p>
          <div className="flex items-center gap-3 text-white/50 text-xs">
            <span>{article.author}</span>
            <span>{article.publishDate}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.viewCount > 10000 ? `${(article.viewCount / 10000).toFixed(1)}万` : article.viewCount}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex flex-col sm:flex-row gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-colors cursor-pointer"
      >
        <div className="w-full sm:w-[200px] h-40 sm:h-[140px] rounded-lg overflow-hidden flex-shrink-0">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs text-primary-500 font-medium bg-primary-50 px-2 py-0.5 rounded-full">
              {article.categoryName}
            </span>
            {article.isHot && (
              <span className="text-xs text-accent-600 font-medium bg-accent-50 px-2 py-0.5 rounded-full flex items-center gap-0.5">
                <i className="ri-fire-line text-[10px]"></i>热门
              </span>
            )}
          </div>
          <h3 className="font-heading text-base font-bold text-foreground-950 mb-1.5 line-clamp-2 group-hover:text-primary-500 transition-colors">
            {article.title}
          </h3>
          <p className="text-foreground-500 text-sm line-clamp-2 mb-3">{article.excerpt}</p>
          <div className="flex items-center gap-3 text-foreground-400 text-xs">
            <span>{article.author}</span>
            <span>{article.publishDate}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.viewCount > 10000 ? `${(article.viewCount / 10000).toFixed(1)}万` : article.viewCount}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i>
              {article.commentCount}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 hover:-translate-y-0.5 cursor-pointer"
    >
      <div className="h-48 overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-primary-500 font-medium bg-primary-50 px-2 py-0.5 rounded-full">
            {article.categoryName}
          </span>
          {article.isHot && (
            <span className="text-xs text-accent-600 font-medium bg-accent-50 px-2 py-0.5 rounded-full flex items-center gap-0.5">
              <i className="ri-fire-line text-[10px]"></i>热门
            </span>
          )}
        </div>
        <h3 className="font-heading text-base font-bold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
          {article.title}
        </h3>
        <p className="text-foreground-500 text-sm line-clamp-2 mb-3">{article.excerpt}</p>
        <div className="flex items-center justify-between text-foreground-400 text-xs">
          <div className="flex items-center gap-2">
            <span>{article.author}</span>
            <span>·</span>
            <span>{article.publishDate}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.viewCount > 10000 ? `${(article.viewCount / 10000).toFixed(1)}万` : article.viewCount}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i>
              {article.commentCount}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}